// Typed client for court bookings, promoted out of the KV bridge to a relational
// table (Stage 2). On the `http` backend these hit the dedicated /bookings
// endpoint (real Postgres); on memory/tests they fall back to the key/value
// Storage the app used before (`bookings_v1`), so behavior off the http backend
// is unchanged. The app (and the seriespill panel) load the full list — on the
// http backend in pages, assembled here (#1138) — and write through the narrow
// commands below.
//
// persistBookings THROWS on a non-2xx write (Storage-first contract) — callers
// are already Storage-first (write before setState) and handle/propagate the error.
import { Storage } from '../storage';
import { apiFetch } from './http';
import { LIST_PAGE_SIZE, fetchAllPages } from './pagedList';
import { OverrideRefusedError, isOverrideRefusal } from './bookingOverride';

const KEY = 'bookings_v1';

/** Rows per request — the club lists' page size, since they share a reader. */
export const BOOKINGS_PAGE_SIZE = LIST_PAGE_SIZE;

/**
 * The club's whole calendar, read IN PAGES (#1138 part 4).
 *
 * Callers still get the one array they always got, in the order the server
 * used to send it — `createdAt` ascending — so no screen changes. What changes
 * is that no single request makes the API materialise the club's whole
 * history at once.
 *
 * Two details keep the answer the same as one read:
 *   - A booking moved between two page reads can be seen twice; the LATER
 *     copy wins, so the list holds each id once.
 *   - A page that fails fails the whole read, with the same `[]` a failed
 *     single read gave. A calendar missing some of its pages would show free
 *     courts that are taken.
 *
 * The page loop is the club lists' (`pagedList.ts`, #1202) — one reader, so
 * the gateway retry and the «could not load» record (#1247) apply to the
 * calendar as well. The server's page ceiling for bookings is 2000
 * (`BOOKINGS_PAGE_MAX`), the same as the lists'.
 */
export async function fetchBookings(): Promise<any[]> {
  if (Storage.backend === 'http') {
    // The order the single read used: oldest first by creation time, a
    // missing `createdAt` read as 0. The reader's sort is stable, so equal
    // `createdAt`s keep the page order.
    return fetchAllPages<any>(
      '/bookings',
      'bookings',
      (a, b) => (Number(a.createdAt) || 0) - (Number(b.createdAt) || 0),
    );
  }
  const r = await Storage.get(KEY);
  return r?.value ? JSON.parse(r.value) : [];
}

/**
 * Whole-collection write — THE LEGACY PATH ONLY (#245).
 *
 * `PUT /bookings` is gone: it upserted from a list the client read EARLIER and
 * deleted every row not in it, so a member who booked a court in between had
 * the booking silently removed. On the API backend every write now goes
 * through the narrow commands below.
 *
 * This still writes the `bookings_v1` blob in memory, unchanged,
 * because that is what the deployed browser runs today. It THROWS on the http
 * backend rather than falling back, so a caller that has not been converted
 * fails loudly here instead of at a 405 it did not expect.
 */
export async function persistBookings(list: any[]): Promise<void> {
  if (Storage.backend === 'http') {
    throw new Error('[bookings] whole-collection write removed (#245) — use the narrow commands');
  }
  await Storage.set(KEY, JSON.stringify(list));
}

/** True when writes must go through the server commands rather than a blob. */
export const bookingsAreServerAuthoritative = () => Storage.backend === 'http';

export interface CreateBookingsIntent {
  bookings: any[];
  /** Removed from the calendar BEFORE validation, and deleted. */
  deleteIds?: string[];
  /** Every booking in this series removed before validation, and deleted. */
  replaceSeriesId?: string;
  adminOverride?: boolean;
  bufferMin?: number;
  /**
   * Proceed even though a court is out of service (#271).
   *
   * Separate from `adminOverride` on purpose — that one relaxes the CALENDAR
   * rules (buffer, smart allocation) and must never carry the power to book a
   * physically broken court. The server refuses this flag from a caller without
   * `court.override_out_of_service`, so sending it is a request, not a decision.
   */
  overrideCourtOutOfService?: boolean;
}

/** Why one booking in the request was not created (#271, #245). */
export interface BookingRefusal {
  court: number | null;
  date: string | null;
  startTime: string | null;
  /** 'court_out_of_service' — the court itself; 'invalid' — the calendar rules. */
  error: 'court_out_of_service' | 'invalid' | string;
  /** The operational note staff wrote, or the engine's own reason. */
  reason: string | null;
  since?: number | null;
  /** Whether THIS caller may proceed anyway — the server decides, not the UI. */
  overridable: boolean;
}

export interface CreateBookingsResult {
  created: number;
  failed: number;
  bookings: any[];
  /** Per-booking reasons. Absent on older servers. */
  refusals?: BookingRefusal[];
}

/**
 * One refusal, as a sentence for whoever is looking at the screen.
 *
 * Shared by create and move, because they refuse for the same reasons and an
 * operator should not be told two different things about the same court. The
 * PATCH response carries one refusal rather than a list; the wording is the
 * same either way.
 */
export function refusalMessage(r: BookingRefusal | null | undefined): string | null {
  if (!r) return null;
  if (r.error === 'court_out_of_service') {
    // The court and its state come FIRST. A bare operational note («Nettet er
    // nede») does not tell an operator which court, or that the court is the
    // reason at all — and when nobody wrote a note there would be nothing left.
    const why = r.reason ? ` (${r.reason})` : '';
    return r.overridable
      ? `Bane ${r.court} er merket ute av drift${why}. Du kan overstyre dette.`
      : `Bane ${r.court} er ute av drift${why}.`;
  }
  // THE EQUIPMENT RIDING WITH THE BOOKING (#914 review). A court move takes its
  // equipment along, and is refused when that cannot be done — the operator is
  // told it is the equipment, not handed a generic rejection.
  if (r.error === 'asset_conflict') {
    return 'Utstyr som er reservert sammen med bookingen, er opptatt på det nye tidspunktet.';
  }
  if (r.error === 'asset_outside_booking') {
    return 'Utstyret som følger bookingen, får ikke plass innenfor det nye tidspunktet.';
  }
  // NO BOOKING BEFORE THE HOURS ARE SAVED (owner decision 2026-10-31). Said as
  // what to do, because the remedy is the operator's own: save the hours.
  if (r.error === 'booking_hours_not_set') {
    return 'Booking er ikke åpnet: lagre åpningstidene i bookinginnstillingene først.';
  }
  // NO PRICE UNTIL ZONES EXIST (#1312, owner decision 2026-11-03): a booking
  // cannot owe money at a club with no saved price zone. In this file's legacy
  // sentence form, like its neighbours; the editor itself no longer sends it.
  if (r.error === 'court_prices_not_set') {
    return 'Prisen er ikke satt: legg inn pris-soner før en booking kan vente på betaling.';
  }
  // A PAYMENT FOR IT IS STILL OPEN (#1087): the court stays where it was
  // priced until the attempt is captured or given up.
  if (r.error === 'booking_payment_open') {
    return 'Bookingen har en betaling som pågår. Vent til den er fullført eller avbrutt før du flytter den.';
  }
  if (r.error === 'asset_booking_changed') {
    return 'Utstyret på bookingen ble endret samtidig. Hent kalenderen på nytt og prøv igjen.';
  }
  return r.reason ? String(r.reason) : null;
}

/**
 * The refusal that matters, as an object: the court's own state first.
 *
 * The MESSAGE is what a human reads; this is what the UI needs to decide
 * whether to offer an override — `overridable` is the server's answer about
 * THIS caller, and the browser must not compute it for itself.
 */
export function firstRefusal(out: CreateBookingsResult): BookingRefusal | null {
  const refusals = out.refusals ?? [];
  return refusals.find((r) => r.error === 'court_out_of_service') ?? refusals[0] ?? null;
}

/** The refusal to show a human: the court's own state first, if there is one. */
export function firstRefusalMessage(out: CreateBookingsResult): string | null {
  return refusalMessage(firstRefusal(out));
}

/**
 * A refusal that carries its structure, not only its sentence.
 *
 * `patchBooking` threw a plain Error, which is all a caller needs to SHOW the
 * refusal and not enough to offer the override the same refusal says is
 * available. The message is unchanged — it is the same sentence create
 * produces, and is pinned by tests.
 */
export class BookingRefusedError extends Error {
  readonly refusal: BookingRefusal | null;
  constructor(message: string, refusal: BookingRefusal | null) {
    super(message);
    this.name = 'BookingRefusedError';
    this.refusal = refusal;
  }
}

/**
 * The WHOLE request refused because it asked for `adminOverride` without
 * `booking.override_constraints` (#949). Typed, so the screen can say that
 * rather than «create failed: 403»; any other 403 falls through unchanged.
 */
async function throwIfOverrideRefused(res: Response): Promise<void> {
  if (res.status !== 403) return;
  // Read once: a 403 throws either way, so nothing after this needs the body.
  const body = await res.json().catch(() => null);
  if (isOverrideRefusal(body)) throw new OverrideRefusedError();
}

/**
 * Create one or many bookings, optionally clearing space first.
 *
 * Partial success is the contract, not an accident: each booking is validated
 * against the ones already accepted in the same batch, and a clash counts as
 * `failed` rather than aborting the rest — a recurring series that collides on
 * one week still books the other nine.
 */
export async function createBookings(intent: CreateBookingsIntent): Promise<CreateBookingsResult> {
  const res = await apiFetch('/bookings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(intent),
  });
  // A WHOLE-REQUEST refusal — the club has not saved its hours — rather than a
  // per-booking one, so it arrives as a 409 instead of inside `refusals`.
  if (res.status === 409) {
    const body = (await res.json().catch(() => null)) as BookingRefusal | null;
    const message = refusalMessage(body);
    if (
      (body?.error === 'booking_hours_not_set' || body?.error === 'court_prices_not_set') &&
      message
    ) {
      throw new BookingRefusedError(message, body);
    }
  }
  await throwIfOverrideRefused(res);
  if (!res.ok) throw new Error('[bookings] create failed: ' + res.status);
  return (await res.json()) as CreateBookingsResult;
}

/** Move or re-time one booking. The server revalidates against the calendar. */
export async function patchBooking(id: string, patch: Record<string, any>): Promise<any> {
  const res = await apiFetch('/bookings/' + encodeURIComponent(id), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (res.status === 409) {
    const body = (await res.json().catch(() => null)) as Partial<BookingRefusal> | null;
    // The same sentence create produces. A move is refused for the same reasons
    // and must not describe them differently — an out-of-service court reads as
    // one here too, rather than as a bare note or a generic rejection (#271).
    throw new BookingRefusedError(
      refusalMessage(body as BookingRefusal) ||
        (body as { reason?: string } | null)?.reason ||
        'Bookingen ble avvist',
      body && body.error ? (body as BookingRefusal) : null,
    );
  }
  await throwIfOverrideRefused(res);
  if (!res.ok) throw new Error('[bookings] patch failed: ' + res.status);
  return await res.json();
}

/**
 * A delete the server REFUSED, with its code — `booking_has_payment` (#526, #284),
 * or since #1310 `payment_cancel_unconfirmed` (Stripe would not confirm the open
 * payment was cancelled), `booking_payment_open` and `payment_cancel_unavailable`.
 * NOTHING was deleted: the server checks under its lock before any row goes.
 *
 * THE MESSAGE IS THE CODE, never a sentence: the screen turns it into one
 * (`errorMessage`), so the language is the reader's and not this module's.
 */
export class BookingDeleteRefusedError extends Error {
  readonly code: string;
  readonly bookings: readonly string[];
  constructor(code: string, bookings: readonly string[]) {
    super(code);
    this.name = 'BookingDeleteRefusedError';
    this.code = code;
    this.bookings = bookings;
  }
}

/**
 * The refusal the calendar answers with a door of its own: the booking holds a
 * verified payment, so «Avbestill og refunder» is what to do instead (#284).
 * A `BookingDeleteRefusedError` too, so one `instanceof` catches every refusal.
 */
export class BookingHasPaymentError extends BookingDeleteRefusedError {
  declare readonly code: 'booking_has_payment';
  constructor(bookings: readonly string[]) {
    super('booking_has_payment', bookings);
    this.name = 'BookingHasPaymentError';
  }
}

async function deleteAnswer(res: Response): Promise<number> {
  if (res.status === 409 || res.status === 503) {
    const body = (await res.json().catch(() => null)) as {
      error?: unknown;
      bookings?: unknown;
    } | null;
    if (typeof body?.error === 'string') {
      const ids = Array.isArray(body.bookings)
        ? body.bookings.filter((b): b is string => typeof b === 'string')
        : [];
      throw body.error === 'booking_has_payment'
        ? new BookingHasPaymentError(ids)
        : new BookingDeleteRefusedError(body.error, ids);
    }
  }
  if (!res.ok) throw new Error('[bookings] delete failed: ' + res.status);
  return ((await res.json()) as { deleted: number }).deleted;
}

export async function deleteBookingsByIds(ids: string[]): Promise<number> {
  if (ids.length === 0) return 0;
  const q = ids.map((i) => 'id=' + encodeURIComponent(i)).join('&');
  return deleteAnswer(await apiFetch('/bookings?' + q, { method: 'DELETE' }));
}

export async function deleteBookingsBySeries(seriesId: string): Promise<number> {
  return deleteAnswer(
    await apiFetch('/bookings?seriesId=' + encodeURIComponent(seriesId), { method: 'DELETE' }),
  );
}
